import { CommonModule } from '@angular/common';
import { Component, Inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import {
  MAT_DIALOG_DATA,
  MatDialogModule,
  MatDialogRef,
} from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';

interface ResourceAttribute {
  id: string;
  name: string;
  slug: string | null;
  type: string;
  external?: boolean;
  resourceTypeId: string;
  order: number | null;
  [key: string]: any;
}

@Component({
  selector: 'app-resourceattribute-dialog',
  templateUrl: './resourceattribute-dialog.component.html',
  standalone: true,
  imports: [
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatSelectModule,
    FormsModule,
    MatDialogModule,
    CommonModule,
  ],
})
export class ResourceAttributeDialogComponent {
  selectDisplayValues: string = '';
  selectSlugValues: string = '';
  selectError: string = '';

  constructor(
    public dialogRef: MatDialogRef<ResourceAttributeDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: Partial<ResourceAttribute>
  ) {
    // If editing and type is select, try to load existing values
    if (data.type === 'select') {
      // Prefer the new fields if present
      if (typeof data['selectDisplayValues'] === 'string') {
        this.selectDisplayValues = data['selectDisplayValues'];
      } else if (data['options']) {
        // fallback for legacy/old data
        try {
          const options = Array.isArray(data['options'])
            ? data['options']
            : JSON.parse(data['options']);
          this.selectDisplayValues = options
            .map((opt: any) => opt.display)
            .join(', ');
        } catch {
          this.selectDisplayValues = '';
        }
      } else {
        this.selectDisplayValues = '';
      }

      if (typeof data['selectSlugValues'] === 'string') {
        this.selectSlugValues = data['selectSlugValues'];
      } else if (data['options']) {
        // fallback for legacy/old data
        try {
          const options = Array.isArray(data['options'])
            ? data['options']
            : JSON.parse(data['options']);
          this.selectSlugValues = options.map((opt: any) => opt.value).join(', ');
        } catch {
          this.selectSlugValues = '';
        }
      } else {
        this.selectSlugValues = '';
      }
    }
  }

  onTypeChange() {
    if (this.data.type === 'select') {
      // If switching to select, try to prefill from existing values if present
      if (typeof this.data['selectDisplayValues'] === 'string') {
        this.selectDisplayValues = this.data['selectDisplayValues'];
      } else {
        this.selectDisplayValues = '';
      }
      if (typeof this.data['selectSlugValues'] === 'string') {
        this.selectSlugValues = this.data['selectSlugValues'];
      } else {
        this.selectSlugValues = '';
      }
      this.selectError = '';
    } else {
      this.selectDisplayValues = '';
      this.selectSlugValues = '';
      this.selectError = '';
    }
  }

  onSubmit() {
    this.selectError = '';
    if (this.data.name) {
      if (this.data.type === 'select') {
        // Validate and process select options
        const displayArr = this.selectDisplayValues
          .split(',')
          .map((v) => v.trim())
          .filter((v) => v.length > 0);
        const slugArr = this.selectSlugValues
          .split(',')
          .map((v) => v.trim())
          .filter((v) => v.length > 0);

        if (displayArr.length === 0 || slugArr.length === 0) {
          this.selectError =
            'Both display and slug values are required for select type.';
          return;
        }
        if (displayArr.length !== slugArr.length) {
          this.selectError =
            'Display values and slug values must have the same number of items.';
          return;
        }

        // Store as fields for backend
        (this.data as any)['selectDisplayValues'] = this.selectDisplayValues;
        (this.data as any)['selectSlugValues'] = this.selectSlugValues;
      } else {
        // Remove select fields if not select
        if ((this.data as any)['selectDisplayValues']) {
          delete (this.data as any)['selectDisplayValues'];
        }
        if ((this.data as any)['selectSlugValues']) {
          delete (this.data as any)['selectSlugValues'];
        }
      }
      this.dialogRef.close(this.data);
    }
  }
}
